import {
  CalendarDate,
  createCalendar,
  DateValue,
  fromDate,
  getLocalTimeZone,
  getWeeksInMonth,
  today,
} from "@internationalized/date";
import { Icon, Spinner, Button as MindlerButton } from "@mindlercare/ui-web";
import { format, getWeek, isSameDay, startOfMonth } from "date-fns";
import { LocalSettingKey, getLocalSetting } from "core/localSettings";
import { RefObject, useRef, useState } from "react";
import {
  AriaCalendarGridProps,
  AriaCalendarProps,
  Placement,
  useCalendar,
  useCalendarCell,
  useCalendarGrid,
  useLocale,
} from "react-aria";
import { useTranslation } from "react-i18next";
import {
  CalendarState,
  useCalendarState,
  useOverlayTriggerState,
} from "react-stately";
import { styled, DefaultTheme, useTheme } from "styled-components";
import { Typography } from "components/typography";
import { ChevronLeftButton, ChevronRightButton } from "./ChevronButtons";
import { PickerPopover } from "./PickerPopover";

interface PatientDate {
  date: Date;
  patientUserId?: string;
  isBooked?: boolean;
}

/**
 * Date picker with information about the number of booked and open slots and feedbacks for each day.
 *
 * Can be given a patientUserId to highlight the slots and feedback days of the patient.
 *
 * Used for selecting a feedback date for a patient.
 *
 * @param value - The currently selected date.
 * @param setValue - Function to set the selected date.
 * @param isOpen - Whether the date picker is open.
 * @param setIsOpen - Function to set whether the date picker is open.
 * @param patientUserId - Optional id of the patient currently being viewed. If provided, the date picker will highlight the slots and feedback days of the patient..
 * @param feedbackDates - The feedback dates of the patients of the user.
 * @param slotDates - The slot dates of the user.
 * @param isFetching - Show a spinner indicating that something is loading.
 * @param focusedMonth - The month that the calendar is currently focused on. This state is hoisted to the parent component to allow using the value in data fetching for example.
 * @param setFocusedMonth - Function to set the focused month.
 * @param triggerRef - Ref to the element that triggers the date picker. Used to position the date picker.
 */
export const DatePickerWithSchedule = ({
  value,
  setValue,
  isOpen,
  setIsOpen,
  patientUserId,
  feedbackDates,
  slotDates,
  isFetching,
  focusedMonth,
  setFocusedMonth,
  triggerRef,
  placement = "bottom",
  offset = 0,
  crossOffset = 0,
}: {
  value: Date | undefined;
  setValue: (date: Date | undefined) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  patientUserId?: string;
  feedbackDates: PatientDate[];
  slotDates: PatientDate[];
  isFetching?: boolean;
  focusedMonth: Date;
  setFocusedMonth: (date: Date) => void;
  triggerRef: RefObject<Element | null>;
  placement?: Placement;
  offset?: number;
  crossOffset?: number;
}) => {
  const overlayState = useOverlayTriggerState({
    isOpen,
    onOpenChange: setIsOpen,
  });

  if (!isOpen) {
    return null;
  }

  return (
    <PickerPopover
      triggerRef={triggerRef}
      state={overlayState}
      placement={placement}
      offset={offset}
      crossOffset={crossOffset}
    >
      <DatePicker
        value={value}
        setValue={setValue}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        patientUserId={patientUserId}
        feedbackDates={feedbackDates}
        slotDates={slotDates}
        isFetching={isFetching}
        focusedMonth={focusedMonth}
        setFocusedMonth={setFocusedMonth}
      />
    </PickerPopover>
  );
};

export const DatePicker = ({
  value,
  setValue,
  setIsOpen,
  patientUserId,
  feedbackDates,
  slotDates,
  isFetching,
  focusedMonth,
  setFocusedMonth,
  saving,
}: {
  value: Date | undefined;
  setValue: (date: Date | undefined) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  patientUserId?: string;
  feedbackDates: PatientDate[];
  slotDates: PatientDate[];
  isFetching?: boolean;
  focusedMonth: Date;
  setFocusedMonth: (date: Date) => void;
  saving?: boolean;
}) => {
  const timeZone = getLocalTimeZone();
  const todayDate = today(timeZone);

  const [selectedDate, setSelectedDate] = useState<DateValue>(
    value ? fromDate(value, timeZone) : todayDate,
  );

  return (
    <>
      <CalendarComponent
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        todayDate={todayDate}
        focusedMonth={focusedMonth}
        setFocusedMonth={setFocusedMonth}
        timeZone={timeZone}
        patientUserId={patientUserId}
        slotDates={slotDates}
        feedbackDates={feedbackDates}
        isFetching={isFetching}
        currentFeedbackDate={value}
      />
      <Footer
        todayDate={todayDate}
        selectedDate={selectedDate}
        setValue={setValue}
        setIsOpen={setIsOpen}
        saving={saving}
      />
    </>
  );
};

const CalendarComponent = ({
  selectedDate,
  setSelectedDate,
  todayDate,
  setFocusedMonth,
  timeZone,
  patientUserId,
  slotDates,
  feedbackDates,
  isFetching,
  currentFeedbackDate,
}: {
  selectedDate: DateValue | undefined;
  setSelectedDate: (date: DateValue) => void;
  todayDate: DateValue;
  focusedMonth: Date;
  setFocusedMonth: (date: Date) => void;
  timeZone: string;
  patientUserId?: string;
  slotDates: PatientDate[];
  feedbackDates: PatientDate[];
  isFetching?: boolean;
  currentFeedbackDate?: Date;
}) => {
  const { locale } = useLocale();
  const { t } = useTranslation();
  const props: AriaCalendarProps<DateValue> = {
    onChange: (value) => setSelectedDate(value),
    value: selectedDate,
    onFocusChange: (value) =>
      setFocusedMonth(startOfMonth(value.toDate(timeZone))),
    minValue: todayDate.add({ days: 1 }),
  };

  const state = useCalendarState({
    ...props,
    locale,
    createCalendar,
  });

  const { calendarProps, prevButtonProps, nextButtonProps, title } =
    useCalendar(props, state);

  return (
    <div {...calendarProps}>
      <HeadingSection>
        <StyledCalendarHeading>{title}</StyledCalendarHeading>
        <ChevronLeftButton {...prevButtonProps} />
        <ChevronRightButton {...nextButtonProps} />
        {isFetching && <Spinner />}
        {currentFeedbackDate &&
          getLocalSetting(LocalSettingKey.ShowDebugInfo) && (
            <CurrentFeedbackDate>
              {t("FEEDBACK_CALENDAR_CURRENT_DATE", {
                date: format(currentFeedbackDate, "yyyy-MM-dd"),
              })}
            </CurrentFeedbackDate>
          )}
      </HeadingSection>
      <CalendarGrid
        state={state}
        slotDates={slotDates}
        feedbackDates={feedbackDates}
        todayDate={todayDate}
        selectedDate={selectedDate}
        timeZone={timeZone}
        patientUserId={patientUserId}
      />
    </div>
  );
};

interface CalendarGridWithState extends AriaCalendarGridProps {
  state: CalendarState;
  slotDates: PatientDate[];
  feedbackDates: PatientDate[];
  todayDate: DateValue;
  selectedDate: DateValue | undefined;
  timeZone: string;
  patientUserId?: string;
}

const CalendarGrid = ({ state, ...props }: CalendarGridWithState) => {
  const { locale } = useLocale();
  const timeZone = getLocalTimeZone();
  const { gridProps, headerProps, weekDays } = useCalendarGrid(props, state);
  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);
  const firstWeekNumber = getWeek(
    startOfMonth(state.visibleRange.start.toDate(timeZone)),
    {
      weekStartsOn: 1, // Monday
      firstWeekContainsDate: 4, // First week contains 4th of January
    },
  );

  return (
    <StyledGrid {...gridProps} cellPadding={0}>
      <thead {...headerProps}>
        <tr>
          <th />
          {weekDays.map((day, index) => (
            <DayHeading key={index}>{day}</DayHeading>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
          <tr key={weekIndex}>
            <WeekNumber>{firstWeekNumber + weekIndex}</WeekNumber>
            {state
              .getDatesInWeek(weekIndex)
              .map((date, i) =>
                date ? (
                  <CalendarGridCellComponent
                    key={i}
                    date={date}
                    state={state}
                    {...props}
                  />
                ) : (
                  <td key={i} />
                ),
              )}
          </tr>
        ))}
      </tbody>
    </StyledGrid>
  );
};

const DayHeading = styled.th`
  text-align: center;
  font-size: ${({ theme }) => theme.FONT.SIZE.MEDIUM};
  font-weight: ${({ theme }) => theme.FONT.WEIGHT.MEDIUM};
  color: ${({ theme }) => theme.COLORS.BLACK_GREY};
  padding-bottom: ${({ theme }) => theme.MARGIN.SM};
`;

const WeekNumber = styled.th`
  padding-right: ${({ theme }) => theme.MARGIN.SM};
  color: ${({ theme }) => theme.COLORS.GRAY};
  font-weight: ${({ theme }) => theme.FONT.WEIGHT.LIGHT};
`;

const CalendarGridCellComponent = ({
  slotDates,
  feedbackDates,
  date,
  todayDate,
  timeZone,
  patientUserId,
  state,
}: {
  slotDates: PatientDate[];
  feedbackDates: PatientDate[];
  date: CalendarDate;
  todayDate: DateValue;
  selectedDate: DateValue | undefined;
  timeZone: string;
  patientUserId?: string;
  state: CalendarState;
}) => {
  const { t } = useTranslation();

  const ref = useRef(null);

  const isFuture = date.compare(todayDate) >= 0;
  const isToday = date.compare(todayDate) === 0;
  const slotsOfDay =
    slotDates.filter((slot) =>
      isSameDay(new Date(slot.date), date.toDate(timeZone)),
    ) ?? [];

  const bookedSlotsOfDay = slotsOfDay.filter((slot) => slot.isBooked);

  const slotsOfDayString = slotsOfDay.length
    ? ` ${t("SLOTS_OF_DAY", {
        bookedSlots: bookedSlotsOfDay.length,
        totalSlots: slotsOfDay.length,
      })}`
    : " 0";

  const feedbacksOfDay = feedbackDates.filter(({ date: feedbackDate }) => {
    return isSameDay(feedbackDate, date.toDate(timeZone));
  });
  const isPatientSlot = slotsOfDay.some(
    (slot) => slot.patientUserId === patientUserId,
  );

  const isPatientFeedback = feedbacksOfDay.some(
    (f) => f.patientUserId === patientUserId,
  );

  const {
    cellProps,
    buttonProps,
    isSelected,
    isOutsideVisibleRange,
    isDisabled,
  } = useCalendarCell(
    { date, isDisabled: isPatientSlot || isPatientFeedback },
    state,
    ref,
  );

  if (isOutsideVisibleRange) {
    return <td />;
  }

  const color: keyof DefaultTheme["COLORS"] =
    isToday || isSelected ? "WHITE" : isFuture ? "DARK_GREY" : "GRAY";

  const hasHeading = !isPatientSlot && !isPatientFeedback;
  return (
    <StyledCalendarCell
      {...cellProps}
      isToday={isToday}
      isHighlighted={isPatientSlot || isPatientFeedback}
      isPatientSlot={isPatientSlot}
      isFeedbackSlot={isPatientFeedback}
      isSelected={isSelected}
      isPast={!isFuture}
    >
      <CalendarCellContent
        {...buttonProps}
        disabled={isDisabled}
        ref={ref}
        hasHeading={hasHeading}
      >
        {!isPatientSlot && !isPatientFeedback ? (
          <CalendarCellHeading>
            <Typography insensitive variant="h4" color={color}>
              {date.day}
            </Typography>
          </CalendarCellHeading>
        ) : null}
        {isPatientSlot ? (
          <>
            <Icon.Video style={{ width: 20, color }} />
            <Typography
              insensitive
              variant="h5"
              color={color}
              sx={{ textAlign: "center" }}
            >
              {t("PATIENT_BOOKING")}
            </Typography>
          </>
        ) : (
          isPatientFeedback && (
            <>
              <Icon.Feedback style={{ width: 20, color }} />
              <Typography
                insensitive
                variant="h5"
                color={color}
                sx={{ textAlign: "center" }}
              >
                {t("PATIENT_ICBT_FEEDBACK")}
              </Typography>
            </>
          )
        )}
        {!isPatientSlot && !isPatientFeedback && isFuture && (
          <>
            <Typography insensitive variant="body" color={color}>
              <Icon.Video style={{ width: 14, color }} />
              {slotsOfDayString}
            </Typography>
            <Typography insensitive variant="body" color={color}>
              <Icon.Feedback style={{ width: 14, color }} />
              {feedbacksOfDay.length}
            </Typography>
          </>
        )}
      </CalendarCellContent>
    </StyledCalendarCell>
  );
};

const Footer = ({
  todayDate,
  selectedDate,
  setValue,
  setIsOpen,
  saving,
}: {
  todayDate: DateValue;
  selectedDate: DateValue | undefined;
  setValue: (date: Date) => void;
  setIsOpen: (isOpen: boolean) => void;
  saving?: boolean;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <FooterSection>
      <Icon.Video style={{ width: 14, color: theme.COLORS.BLACK_GREY }} />
      <Typography
        variant="body-small"
        color="DARK_GREY"
        sx={{ marginLeft: theme.MARGIN.SM, marginRight: theme.MARGIN.MD }}
        insensitive
      >
        {t("VIDEO_TIMESLOTS")}
      </Typography>
      <Icon.Feedback style={{ width: 14, color: theme.COLORS.BLACK_GREY }} />
      <Typography
        variant="body-small"
        color="DARK_GREY"
        sx={{ marginLeft: theme.MARGIN.SM, marginRight: "auto" }}
        insensitive
      >
        {t("ICBT_FEEDBACK_SCHEDULED")}
      </Typography>
      <MindlerButton variant="secondary" onClick={() => setIsOpen(false)}>
        {t("CANCEL")}
      </MindlerButton>
      <div style={{ width: "10px" }} />
      <MindlerButton
        variant="primary"
        disabled={
          !selectedDate || selectedDate.compare(todayDate) < 0 || saving
        }
        onClick={() => {
          if (selectedDate) {
            setValue(selectedDate.toDate(getLocalTimeZone()));
            setIsOpen(false);
          }
        }}
      >
        {t("SAVE")}
      </MindlerButton>
    </FooterSection>
  );
};

const CalendarCellContent = styled.div<{
  disabled: boolean;
  hasHeading: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ hasHeading }) =>
    hasHeading ? "flex-start" : "center"};
  padding: 10px 10px 0px 10px;
  height: 100%;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
`;

const CalendarCellHeading = styled.div`
  display: flex;
  justify-content: space-between;
`;

const HeadingSection = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.MARGIN.XL};
`;

const CurrentFeedbackDate = styled.span`
  margin-left: auto;
  font-style: italic;
  font-size: ${({ theme }) => theme.FONT.SIZE.SMALL};
  color: ${({ theme }) => theme.COLORS.GRAY_DARK};
`;

const FooterSection = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${({ theme }) => theme.MARGIN.MD};
`;

const StyledCalendarHeading = styled.h1`
  font-size: ${({ theme }) => theme.FONT.SIZE.LARGE};
  font-weight: ${({ theme }) => theme.FONT.WEIGHT.MEDIUM};
  margin: 0;
  color: ${({ theme }) => theme.COLORS.BLACK_GREY};
`;

const StyledGrid = styled.table`
  border-spacing: 0;
  border-collapse: collapse;
  padding: 0;

  td {
    padding: 0;
  }
`;

const StyledCalendarCell = styled.td<{
  isToday: boolean;
  isHighlighted: boolean;
  isPatientSlot: boolean;
  isFeedbackSlot: boolean;
  isSelected: boolean;
  isPast: boolean;
}>`
  width: 100px;
  height: 100px;
  align-items: center;
  justify-content: center;
  transition: background-color 100ms;
  ${({ isPatientSlot, isFeedbackSlot, isSelected, isToday, isPast, theme }) =>
    isToday
      ? getTodayCalendarCellStyle(theme)
      : isPast
        ? getPastCalendarCellStyle(theme)
        : isPatientSlot || isFeedbackSlot
          ? getBookedCalendarCellStyle(theme)
          : isSelected
            ? getSelectedCalendarCellStyle(theme)
            : getFutureCalendarCellStyle(theme)}
`;

const getBookedCalendarCellStyle = (theme: DefaultTheme) => `
  background: ${theme.COLORS.TERTIARY_LIGHT};
  border: 0.5px solid ${theme.COLORS.GRAY};
  color: ${theme.COLORS.GRAY_BLACK};
  cursor: pointer;
`;

const getTodayCalendarCellStyle = (theme: DefaultTheme) => `
  background: ${theme.COLORS.BLACK};
  border: null;
  color: ${theme.COLORS.WHITE};
  cursor: disabled;
`;

const getPastCalendarCellStyle = (theme: DefaultTheme) => `
  background: ${theme.COLORS.WHITE};
  border: 0.5px solid ${theme.COLORS.GRAY};
  color: ${theme.COLORS.GRAY};
  cursor: disabled;
`;

const getFutureCalendarCellStyle = (theme: DefaultTheme) => `
  background: ${theme.COLORS.WHITE};
  border: 0.5px solid ${theme.COLORS.GRAY};
  color: ${theme.COLORS.GRAY_BLACK};
  cursor: pointer;
  &:hover {
    background-color: ${theme.COLORS.GRAY_LIGHT};
  }
`;

const getSelectedCalendarCellStyle = (theme: DefaultTheme) => `
  background: ${theme.COLORS.ACTION_BLUE};
  border: none;
  color: ${theme.COLORS.WHITE};
  cursor: pointer;
`;

export const StyledReSelectedCalendarCell = styled(StyledCalendarCell)`
  background: ${({ theme }) => theme.COLORS.LIGHT_ACTION_BLUE};
  border: 0.5px solid ${({ theme }) => theme.COLORS.ACTION_BLUE};
  color: ${({ theme }) => theme.COLORS.BLUE_DARK};
  cursor: pointer;
`;
